import qs from "qs"
import { Fragment } from "react"
import { NavLink, Outlet, useRouteLoaderData } from "react-router"

import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { buttonVariants } from "@/components/ui/button"
import { hasPermission } from "@/machines/Authmachine"
import { fetchAPI, handleResponse } from "@/utils"
import { config } from "@/utils/config"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { getStorageKey } from "@/utils/summarizer-config"

import { SummarizerEditor } from "./SummarizerEditor"

import type { RootLoader } from "@/routes/root"
import type { DocumentClassificationType } from "@/utils/types"

const routeId = "summarizer-configuration"
const sessionKey = "summarizer-configuration-filter-storage"

const onBeforeSubmit: OnBeforeSubmit = (formData, reason) => {
  if (reason.type === "field" && reason.name === "organisation") formData.delete("project")
}

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No Organisations Found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>

  const storedOrganisation = filters.raw?.organisation
  const loadedOrganisation =
    storedOrganisation != null && organisations.includes(storedOrganisation) ? storedOrganisation : organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)

  const storedProject = filters.raw?.project
  const loadedProject = storedProject != null && projects.includes(storedProject) ? storedProject : (projects[0] ?? "")

  let classifications: DocumentClassificationType[] = []
  let classificationsFailed = false

  if (loadedProject !== "") {
    try {
      classifications = await fetchAPI<DocumentClassificationType[]>(
        `${config.viewerApiUrl}/api/branddocumentClassification/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedProject)}`,
      ).then(handleResponse)
    } catch {
      classificationsFailed = true
    }
  }

  return { loadedOrganisation, loadedProject, projects, classifications, classificationsFailed }
}

export function SummarizerConfiguration() {
  return (
    <Fragment>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-4">
        <nav aria-label="Summarizer views" className="flex w-full min-w-0 gap-1 rounded-md border p-1 sm:w-auto">
          <NavLink
            to="."
            end
            className={({ isActive }) =>
              buttonVariants({ variant: isActive ? "default" : "ghost", size: "sm", className: "flex-1 sm:flex-none" })
            }
          >
            Configuration
          </NavLink>
          <NavLink
            to="test"
            className={({ isActive }) =>
              buttonVariants({ variant: isActive ? "default" : "ghost", size: "sm", className: "flex-1 sm:flex-none" })
            }
          >
            Test
          </NavLink>
        </nav>
      </div>
      <Outlet />
    </Fragment>
  )
}

function Main() {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const rootData = useRouteLoaderData<RootLoader>("root")

  if (data == null) return null

  if (data.loadedProject === "") {
    return <p className="py-12 text-center text-muted-foreground">No brands available for this client.</p>
  }

  const permissionScope = { organisation: data.loadedOrganisation, project: data.loadedProject }
  const canWrite = hasPermission("writeAccess", permissionScope)
  const canDelete = hasPermission("deleteAccess", permissionScope)
  const filterControls =
    rootData != null ? (
      <div className="min-w-0 flex-1">
        <FiltersWrapper
          submitOnChange
          canReset={false}
          sessionKey={sessionKey}
          onBeforeSubmit={onBeforeSubmit}
          className="max-lg:flex-col max-lg:items-stretch max-lg:*:w-full max-lg:[&_[data-slot=select-trigger]]:w-full max-lg:[&_[data-slot=select-trigger]]:min-w-0 max-lg:[&_[data-slot=select-value]]:min-w-0 max-lg:[&_[data-slot=select-value]]:truncate max-lg:[&>[aria-label]]:flex-col max-lg:[&>[aria-label]]:items-stretch"
        >
          {rootData.organisations.length > 1 && (
            <FiltersWrapper.Select
              name="organisation"
              label="Client Name"
              defaultValue={data.loadedOrganisation}
              items={rootData.organisations}
            />
          )}
          {data.projects.length > 1 && (
            <FiltersWrapper.Select
              name="project"
              label="Brand"
              defaultValue={data.loadedProject}
              items={data.projects}
            />
          )}
        </FiltersWrapper>
      </div>
    ) : null

  return (
    <Fragment>
      {data.classificationsFailed && (
        <p role="alert" className="text-sm text-destructive">
          Could not load the document classifications for this brand. Labels and search terms can still be edited.
        </p>
      )}
      <SummarizerEditor
        filterControls={filterControls}
        key={JSON.stringify([
          data.loadedOrganisation,
          data.loadedProject,
          data.classifications.map(({ name }) => name),
        ])}
        storageKey={getStorageKey(data.loadedOrganisation, data.loadedProject)}
        classifications={data.classifications}
        classificationsFailed={data.classificationsFailed}
        canWrite={canWrite}
        canDelete={canDelete}
      />
    </Fragment>
  )
}

SummarizerConfiguration.Main = Main
SummarizerConfiguration.loader = loader
SummarizerConfiguration.routeId = routeId
